import React from 'react';
import { Link } from 'react-router-dom';
import { communityUrls } from 'urls';
import { pluralize } from 'util/stringUtils';
import { useCommunitySummary } from 'apis/CompanyAPI/communities/useCommunitySummary';
import { getOrUndefined } from 'util/resource';
import DashboardMessage from 'pages/Dashboard/sections/messages/DashboardMessage';
import Logo from 'ui/domain/Logo';
import CompanyMastersOnlyTooltip from 'ui/domain/tooltips/CompanyMastersOnlyTooltip';
import { contentSpacing } from 'ui/theme/themeConstants';
import { useCloseableMessage } from 'apis/CompanyAPI/users/useUiState';

interface Props {
  communitySlug: string;
  users: number;
  companies: number;
  showToolTip?: boolean;
  className?: string;
}

function getLink(pendingCompanies: number, communitySlug: string, isApplicationCommunity: boolean) {
  if (isApplicationCommunity) {
    return `/communities/${communitySlug}/overview/pending`;
  }
  if (pendingCompanies > 0) return communityUrls.manageMembers.companies(communitySlug, 'Pending');
  else return communityUrls.manageMembers.users(communitySlug, 'requests');
}

export default function CommunityRequests({ communitySlug, users, companies, showToolTip = false, className }: Props) {
  const { resource: communityResource } = useCommunitySummary(communitySlug);
  const community = getOrUndefined(communityResource);
  const closeableMessage = useCloseableMessage(`community-admin-pending-requests-${communitySlug}`);

  if (!community) {
    return null;
  }

  const isApplicationCommunity = community.configuration.companyApplicationIsEnabled;

  // For application communities, only show if there are companies
  // For regular communities, only show if there are users or companies
  if (isApplicationCommunity ? companies === 0 : users === 0 && companies === 0) {
    return null;
  }

  if (closeableMessage.isClosed) {
    return null;
  }

  return (
    <DashboardMessage
      style={{ position: 'relative' }}
      className={className}
      topElement={<Logo url={community.details.logoURL} type="community" size={70} />}
      onClose={closeableMessage.closeMessage}
    >
      <div className="text-body">
        {isApplicationCommunity ? (
          <>
            {community.details.name} has{' '}
            <span className="text-weight-medium">
              {companies} pending {pluralize(companies, 'application')}
            </span>
          </>
        ) : (
          <>
            {community.details.name} has{' '}
            {users > 0 && (
              <>
                <span className="text-weight-medium">
                  {users} {pluralize(users, 'user')}
                </span>
              </>
            )}
            {companies > 0 && users > 0 && ' and '}
            {companies > 0 && (
              <>
                <span className="text-weight-medium">
                  {companies} {pluralize(companies, 'company', 'companies')}
                </span>
              </>
            )}
            {' requesting to join - they are waiting for your review.'}
          </>
        )}
        {showToolTip && (
          <span style={{ position: 'absolute', right: contentSpacing, top: contentSpacing }}>
            <CompanyMastersOnlyTooltip title="Only visible for community admins" />
          </span>
        )}
      </div>
      <Link className="text-link" to={getLink(companies, communitySlug, isApplicationCommunity)}>
        {isApplicationCommunity ? 'View pending applications' : 'View requests'}
      </Link>
    </DashboardMessage>
  );
}
